import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import { ClientFormDto, type ClientFormInterface } from '@zaoblako/shared/modules/client/dtos/client/client-form.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { ClientDeletingModalComponent } from '@zaoblako/web/modules/client/dumbs/client-form/client-deleting-modal/client-deleting-modal.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ContragentMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contragent-multi-select/contragent-multi-select.component';
import { HdTaskTypeMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-multi-select/hd-task-type-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable, of } from 'rxjs';

@Component({
  selector: 'crm-client-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonModule,
    SpinnerComponent,
    HdTaskTypeMultiSelectComponent,
    ContragentMultiSelectComponent,
    InputNumberModule,
    FormsModule
  ],
  templateUrl: './client-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientFormComponent extends BaseFormComponent<ClientFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public client: ClientFormInterface = { name: null };
  public dto = ClientFormDto;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public isHelpdeskAvailable = false;
  public helpdeskTaskTypes: HelpdeskTaskTypeFindInterface[] = [];

  private ref: DynamicDialogRef;

  public constructor(
    private readonly clientService: ClientService,
    private readonly formStoreService: FormStoreService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    public readonly globalService: GlobalService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit() {
    this.isHelpdeskAvailable = (this.globalService.globalInfo.tenantInfo?.ratePlan?.availableModules || []).includes(
      'HELPDESK'
    );

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.isHelpdeskAvailable
        ? this.helpDeskTaskTypeService.find<HelpdeskTaskTypeFindInterface>()
        : of(<HelpdeskTaskTypeFindInterface[]>[])
    ]).subscribe(([helpdeskTaskTypes]) => {
      this.helpdeskTaskTypes = helpdeskTaskTypes;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy() {
    this.ref?.destroy();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.clientService
      .findById<ClientFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        select: ['id', 'name', 'contragents', 'helpdeskTaskTypes', 'residentCarLimit']
      })
      .subscribe({
        next: (client) => {
          this.client = client;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveClient() {
    if (this.client.helpdeskTaskTypes?.length) {
      this.client = { ...this.client, helpdeskTaskTypes: this.client.helpdeskTaskTypes };
    }

    if (this.checkValidate(this.client).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.clientService.update<ClientFormInterface, ClientFindInterface>(this.client).subscribe({
          next: (client) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Клиент "${this.client.name}" обновлен`);
            this.formStoreService.saveNext(client, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.clientService.create<ClientFindInterface>(this.client).subscribe({
          next: (client) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Клиент создан');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/client/client/card/' + client.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(client, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteClient() {
    this.ref = this.dialogService.open(ClientDeletingModalComponent, {
      header: 'Удаление клиента',
      contentStyle: { overflow: 'auto' },
      styleClass: 'w-4',
      data: {
        previousClientId: this.formStoreService.getState.forms[this.index]?.entityId
      }
    });

    this.ref.onClose.subscribe((client: ClientFindInterface) => {
      if (client) {
        this.formStoreService.setLoading(true, this.index);

        this.clientService
          .deleteClient(this.formStoreService.getState.forms[this.index]?.entityId, { delegateTo: client?.id })
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Клиент "${this.client?.name}" удален`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public unlockClient() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить клиента "${this.client?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.clientService.patch(this.client as any).subscribe({
          next: () => {
            this.client = { ...this.client, deleted: null } as ClientFindInterface;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Клиент "${this.client?.name}" восстановлен`);
            this.formStoreService.saveNext(this.client, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
